import * as React from 'react';

import { OrganizationFeature, type Organization } from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { isPricingFeatureAvailable } from '@gitbook/app-pricing';
import { Icon } from '@gitbook/spine-icons';
import { Description, EmptyState, renderIcon } from '@gitbook/spine-react';
import { assertNever } from '@gitbook/util-ts';

import {
    type AISearchRecommendedQuestionItem,
    useAISearchRecommendedQuestions,
    useRecentAISearches,
} from '../../AISearch';
import {
    SearchModalResults,
    type SearchResultGroupInfo,
    type SearchResultInfo,
    useRecentSearches,
} from '../../Search';

interface SuggestionRecentSearch {
    type: 'recent-search';
    query: string;
}

interface SuggestionRecentAISearch {
    type: 'recent-ai-search';
    query: string;
}

type Suggestion =
    | SuggestionRecentSearch
    | SuggestionRecentAISearch
    | AISearchRecommendedQuestionItem;

/**
 * Empty state when no query have been provided.
 */
export function AppSearchBlankslate(props: {
    organization: Organization | null;
    onUpdateQuery: (query: string) => void;
    onSubmitAIQuery: (query: string) => void;
}): React.ReactElement {
    const { organization, onUpdateQuery, onSubmitAIQuery } = props;

    const aiEnabled =
        !!organization &&
        isPricingFeatureAvailable(organization, { featureKey: OrganizationFeature.AiLens });

    const recentSearches = useRecentSearches();
    const recentAISearches = useRecentAISearches();

    const aiRecommendedQuestions = useAISearchRecommendedQuestions(
        organization && aiEnabled
            ? {
                  organization: organization.id,
              }
            : null
    );

    const onOpenSuggestion = (item: Suggestion, index: number) => {
        switch (item.type) {
            case 'recent-search': {
                onUpdateQuery(item.query);
                analytics.logEvent({
                    type: 'search_pick_suggestion_recent_search',
                    suggestionIndex: index,
                });
                break;
            }
            case 'recent-ai-search': {
                onSubmitAIQuery(item.query);
                analytics.logEvent({
                    type: 'search_pick_suggestion_recent_ai_search',
                    suggestionIndex: index,
                });
                break;
            }
            case 'recommended-question':
                analytics.logEvent({
                    type: 'search_pick_suggestion_question',
                });
                onSubmitAIQuery(item.question);
                break;
            default:
                assertNever(item);
        }
    };

    const renderSuggestionItem = React.useCallback(
        (item: Suggestion): SearchResultInfo => {
            switch (item.type) {
                case 'recent-search':
                    return {
                        icon: renderIcon(Icon.Search, { size: 16, className: 'text-neutral-low' }),
                        title: item.query,
                        action: 'Search',
                    };
                case 'recent-ai-search':
                    return {
                        icon: renderIcon(Icon.SearchOrAsk, {
                            size: 16,
                            className: 'text-neutral-low',
                        }),
                        title: item.query,
                        action: 'Ask GitBook',
                    };
                case 'recommended-question':
                    return {
                        icon: renderIcon(Icon.SearchOrAsk, {
                            size: 16,
                            className: 'text-neutral-low',
                        }),
                        title: item.question,
                        action: 'Ask GitBook',
                        disabled: !aiEnabled,
                    };
                default:
                    assertNever(item);
            }
        },
        [aiEnabled]
    );

    const suggestions = React.useMemo(() => {
        const recentAISearchesSuggestions = aiEnabled
            ? recentAISearches.map((query) => ({
                  type: 'recent-ai-search',
                  query,
              }))
            : [];
        const recentSearchesSuggestions = recentSearches
            .filter((query) => !recentAISearches.includes(query))
            .map((query) => ({
                type: 'recent-search',
                query,
            }));

        // From recent searches, we want 2 AI searches and 1 reg. searches. This holds only if enough recentAISearches are available, otherwise we fill up with recentSearches.
        const numAISearches = Math.min(2, recentAISearchesSuggestions.length);
        const numSearches = 3 - numAISearches;
        const recentSearchSuggestions = [
            ...(aiEnabled ? recentAISearchesSuggestions.slice(0, numAISearches) : []),
            ...recentSearchesSuggestions.slice(0, numSearches),
        ];

        return [...recentSearchSuggestions, ...(aiEnabled ? aiRecommendedQuestions : [])];
    }, [recentSearches, recentAISearches, aiRecommendedQuestions, aiEnabled]);

    if (suggestions.length === 0) {
        return (
            <EmptyState>
                <Description>Type something to search.</Description>
            </EmptyState>
        );
    }

    return (
        <div className="mb-4 flex flex-1 flex-col">
            <SearchModalResults
                data={suggestions}
                getKey={getSuggestionKey}
                renderItem={renderSuggestionItem}
                renderGroup={renderSuggestionGroup}
                groupItem={groupSuggestionItem}
                getGroupKey={getSuggestionGroupKey}
                onOpen={onOpenSuggestion}
                className="px-3"
            />
        </div>
    );
}

function getSuggestionKey(item: Suggestion): string {
    switch (item.type) {
        case 'recent-search':
        case 'recent-ai-search':
            return `recent-search:${item.query}`;
        case 'recommended-question':
            return `recommended-question:${item.question}`;
        default:
            assertNever(item);
    }
}

function groupSuggestionItem(item: Suggestion): string {
    switch (item.type) {
        case 'recent-search':
        case 'recent-ai-search':
            return 'Recents';
        case 'recommended-question':
            return 'Suggested for you';
        default:
            assertNever(item);
    }
}

function renderSuggestionGroup(group: string): SearchResultGroupInfo {
    return { title: group };
}

function getSuggestionGroupKey(group: string): string {
    return group;
}
